import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import type { NonExecutivesFeePolicyData } from '@shared/types';
import api from '../../../../api.js';

interface NonExecutivesFeePolicyState {
  data: NonExecutivesFeePolicyData | null;
  loading: boolean;
  error: string | null;
  selectedDdlPeriodData: [];
  selectedYear: number;
  availableYears: number[];
  hasData: boolean;
}

const currentYear = new Date().getFullYear();
const defaultYears = Array.from({ length: 12 }, (_, i) => currentYear - i);

const initialState: NonExecutivesFeePolicyState = {
  data: null,
  loading: false,
  error: null,
  selectedDdlPeriodData: [],
  selectedYear: currentYear - 1,
  availableYears: defaultYears,
  hasData: true, // Default to true to show tab until we know otherwise
};

export const GetNonExecutiveFeePolicyData = async (companyId: string, year: number | null) => {
  const url = year
    ? `/api/v2/non-executives-fee-policy/${companyId}?year=${year}`
    : `/api/v2/non-executives-fee-policy/${companyId}`;
  const response = await api.get(url);
  return response.data;
};

export const fetchNonExecutivesFeePolicy = createAsyncThunk(
  'nonExecutivesFeePolicy/fetch',
  async ({ companyId, year }: { companyId: string; year: number | null }) => {
    return GetNonExecutiveFeePolicyData(companyId, year);
  },
);

const NonExecutivesFeePolicySlice = createSlice({
  name: 'nonExecutivesFeePolicy',
  initialState,
  reducers: {
    resetNonExecutiveFeePolicy: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.hasData = true; // Reset to default value
    },
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNonExecutivesFeePolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNonExecutivesFeePolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload?.data ?? null;
        // Check if company is EU based on compensation_source field
        const compensationSource = action.payload?.data?.entity?.compensation_source;
        state.hasData = compensationSource?.toLowerCase() === 'eu';
        state.error = null;
      })
      .addCase(fetchNonExecutivesFeePolicy.rejected, (state, action) => {
        state.loading = false;
        state.hasData = false;
        state.error = action.error.message ?? 'Failed to load non-executive fee policy data';
      });
  },
});

export const { resetNonExecutiveFeePolicy, setSelectedYear } = NonExecutivesFeePolicySlice.actions;

export default NonExecutivesFeePolicySlice.reducer;
